import { apiRequest, apiRequestSSE, ApiError } from '@/shared/api';

export interface KnowledgeEntity {
  label: string;
  id: string;
  topic?: string;
  domain?: string;
  locale?: string;
  props: Record<string, unknown>;
}

export interface KnowledgeEdge {
  type: string;
  from_label: string;
  from_id: string;
  to_label: string;
  to_id: string;
}

export interface KnowledgeSnapshot {
  entities: KnowledgeEntity[];
  edges: KnowledgeEdge[];
  counts_by_label: Record<string, number>;
  total_entities: number;
}

export interface KnowledgeChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface KnowledgePendingMutation {
  action: string;
  summary: string;
  label?: string;
  id?: string;
  domain?: string;
  locale?: string;
  props?: Record<string, unknown>;
  edge_type?: string;
  from_label?: string;
  from_id?: string;
  to_label?: string;
  to_id?: string;
}

export interface KnowledgeChatResult {
  reply: string;
  pending_mutations?: KnowledgePendingMutation[];
  applied?: string[];
  graph_changed: boolean;
}

export interface KnowledgeSnapshotImportResult {
  cleared: number;
  entities_upserted: number;
  edges_created: number;
  batches: number;
  counts_by_label?: Record<string, number>;
}

export async function exportKnowledge(): Promise<KnowledgeSnapshot> {
  return apiRequest<KnowledgeSnapshot>('/knowledge/export');
}

export async function importKnowledgeSnapshot(input: {
  entities: KnowledgeEntity[];
  edges: KnowledgeEdge[];
  clear_first?: boolean;
}): Promise<KnowledgeSnapshotImportResult> {
  return apiRequest<KnowledgeSnapshotImportResult>('/knowledge/import-snapshot', {
    method: 'POST',
    body: {
      clear_first: input.clear_first ?? true,
      entities: input.entities,
      edges: input.edges,
    },
  });
}

export async function chatKnowledge(
  input: { messages: KnowledgeChatMessage[]; allow_mutations?: boolean },
  options?: { signal?: AbortSignal },
): Promise<KnowledgeChatResult> {
  let result: KnowledgeChatResult | null = null;
  let streamError: string | null = null;

  await apiRequestSSE('/knowledge/chat', {
    method: 'POST',
    body: {
      messages: input.messages,
      allow_mutations: Boolean(input.allow_mutations),
    },
    signal: options?.signal,
    onEvent: (event, data) => {
      if (event === 'result' && data && typeof data === 'object') {
        const payload = data as KnowledgeChatResult;
        result = {
          reply: payload.reply ?? '',
          pending_mutations: payload.pending_mutations,
          applied: payload.applied,
          graph_changed: Boolean(payload.graph_changed),
        };
        return;
      }
      if (event === 'error') {
        if (data && typeof data === 'object' && 'error' in data) {
          const message = (data as { error: unknown }).error;
          streamError = typeof message === 'string' ? message : 'Chat failed';
        } else {
          streamError = 'Chat failed';
        }
      }
    },
  });

  if (streamError) {
    throw new ApiError(streamError, 500);
  }
  if (!result) {
    throw new ApiError('Knowledge chat ended without a result', 500);
  }
  return result;
}
